import React, { useMemo, useState } from 'react';
import { Button, Tooltip, useTheme } from '@mui/material';
import { Api as ApiIcon } from '@mui/icons-material';
import { AnalyticsIngestApiDialog } from '../Settings/AnalyticsIngestApiDialog';
import type { FilterDef, ReportDefinition } from '../Settings/reportDsl';
import { deriveIngestPayloadFromReportDsl } from './deriveIngestPayloadFromReportDsl';
import { reportsPanelToolbarButtonSx } from './reportsToolbarSurface';

export interface ReportPanelApiActionProps {
  report: ReportDefinition;
  tenantId: string;
  siteId?: string;
  runtimeFilters?: FilterDef[];
  /**
   * When false, renders nothing. Defaults to true so the API icon always
   * shows like Refresh (independent of Edit permissions).
   */
  visible?: boolean;
}

/**
 * Icon button that opens a Swagger-style ingest API dialog with a payload
 * derived from this panel's Report DSL filters.
 */
export const ReportPanelApiAction: React.FC<ReportPanelApiActionProps> = ({
  report,
  tenantId,
  siteId,
  runtimeFilters,
  visible = true,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const derived = useMemo(
    () => deriveIngestPayloadFromReportDsl({ report, runtimeFilters }),
    [report, runtimeFilters],
  );

  if (!visible) return null;

  const toolbarIconButtonSx = reportsPanelToolbarButtonSx(theme, {
    minWidth: 36,
    width: 36,
    height: 36,
    p: 0,
    borderRadius: 1,
  });
  const panelToolbarButtonColor = theme.palette.mode === 'light' ? undefined : 'secondary' as const;

  return (
    <>
      <Tooltip title="Ingest API for this panel">
        <Button
          variant="outlined"
          color={panelToolbarButtonColor}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          aria-label="Panel ingest API"
          sx={toolbarIconButtonSx}
        >
          <ApiIcon fontSize="small" />
        </Button>
      </Tooltip>
      <AnalyticsIngestApiDialog
        open={open}
        onClose={() => setOpen(false)}
        tenantId={tenantId}
        siteId={siteId || ''}
        reportName={report.name}
        examplePayloadOverride={derived.examplePayload}
        caveats={derived.caveats}
        deriveConfidence={derived.confidence}
        unionExamples={derived.unionExamples.map((u) => ({
          label: u.label,
          examplePayload: u.examplePayload,
        }))}
      />
    </>
  );
};

export default ReportPanelApiAction;
